import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Divider from "@mui/material/Divider";
import closeIcons from "@assets/images/logo/closeIcon.svg";
import MuiButton from "@components/Button/MuiButton";
import check_circle from "@assets/images/logo/check_circle.svg";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { AppDispatch, RootState } from "@store/store";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import { formatReceiptDate } from "../../utils/formatNocDate";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50vw",
  maxWidth: "100%",
  maxHeight: "80vh",
  display: "flex",
  flexDirection: "column",
  borderRadius: ".75rem",
  bgcolor: "background.paper",
  boxShadow: 24,
  padding: 0,
  overflowY: "auto",
};

interface PreviewActionModalProps {
  open: boolean;
  onClose: () => void;
  fetchROW?: (id: number) => void;
  fetchAccess?: (id: number) => void;
  receiptDetails?: any[];
  applicationId?: number;
}

const getReceiptLabel = (stageId: number) => {
  if (stageId === 1) return "Processing Fee Receipt";
  if (stageId === 2) return "Provisional Fee Receipt";
  if (stageId === 3) return "Final Fee Receipt";
  return "Fee Receipt";
};

const getHeaderSubmissionFee = (stageId: number) => {
  if (stageId === 1) return "Application Submission Fee";
  if (stageId === 2) return "Provisional NOC Fee";
  if (stageId === 3) return "Final NOC Fee";
  if (stageId === 4) return "Renewal NOC Fee";
  return "Application Submission Fee";
};

export default function PreviewActionModal({
  open: modalOpen,
  onClose: handleClose,
  fetchROW,
  fetchAccess,
  receiptDetails,
}: PreviewActionModalProps) {
  const [searchParams] = useSearchParams();
  let row = searchParams.get("row");
  let access = searchParams.get("access");
  const dispatch = useDispatch<AppDispatch>();
  const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(true);

  const receiptRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isROWPresent = location.pathname.includes("row");
  const provisionalNOCAmount = 20000;

  const { applicationDetailsROW, loading, error } = useSelector(
    (state: RootState) => state.applicationDetailsROW,
  );

  const { applicationDetails } = useSelector(
    (state: RootState) => state.applicationDetails,
  );

  const modalHeaderLabel = React.useMemo(() => {
    if (!Array.isArray(receiptDetails) || receiptDetails.length === 0)
      return "Fee Receipt";

    const stageId = receiptDetails[0]?.paymentStageId;

    if (stageId === 1) return "Processing Fee Receipt";
    if (stageId === 2) return "Provisional Fee Receipt";
    if (stageId === 3) return "Final Fee Receipt";

    return "Fee Receipt";
  }, [receiptDetails]);

  if (!applicationDetailsROW && !applicationDetails) return;

  const { application_code } = applicationDetails || applicationDetailsROW;

  const handleCancel = () => {
    handleClose();
    setOpenSuccessModal(false);
  };

  const handleDownloadReceipt = async () => {
    if (!receiptRef.current) return;

    const html2canvas = (await import("html2canvas")).default;
    const jsPDF = (await import("jspdf")).default;

    const canvas = await html2canvas(receiptRef.current, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("Receipt.pdf");
  };

  return (
    <>
      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby="preview-modal-title"
        aria-describedby="preview-modal-desc"
        disableAutoFocus
      >
        <Box sx={{ ...style }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1.5rem 1.5rem 1rem 1.5rem",
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "#fff",
            }}
          >
            <p
              id="preview-modal-title"
              style={{
                width: "100%",
                fontWeight: "bold",
                fontSize: "16px",
                textAlign: "center",
              }}
            >
              {modalHeaderLabel}
            </p>
            <button
              onClick={handleClose}
              style={{
                cursor: "pointer",
                border: "none",
                background: "transparent",
                position: "absolute",
                right: "1.5rem",
                top: "1.5rem",
                padding: 0,
              }}
            >
              <img
                src={closeIcons}
                alt="Close icon"
                style={{ width: 20, height: 20 }}
              />
            </button>
          </div>
          <Divider style={{ marginBottom: "0.5rem" }} />

          {/* Content */}
          <div
            ref={receiptRef}
            id="preview-modal-desc"
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.15rem",
              color: "#2E2E2E",
              background: "#fff",
            }}
          >
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: 1,
                p: 3,
                width: "100%",
              }}
            >
              {Array.isArray(receiptDetails) && receiptDetails.length > 0 ? (
                receiptDetails.map((data: any) => (
                  <Box key={data?.receiptNumber} sx={{ mb: 4 }}>
                    {/* Receipt Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <img
                          src={require("@assets/images/national-emblem-logo.png")}
                          alt="National Emblem Logo"
                          style={{
                            height: "40px",
                            width: "100px",
                            marginRight: "10px",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#222" }}
                        >
                          Ministry of Road Transport and Highways
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#222", fontSize: 14 }}
                      >
                        {getReceiptLabel(data?.paymentStageId)}
                      </Typography>
                    </Box>
                    {/* Receipt Info */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Receipt No:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {data?.receiptNumber || "N/A"}
                          </span>
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Transaction ID:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {data?.transactionId || "N/A"}
                          </span>
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Date &amp; Time:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {(data?.transactionDate &&
                              formatReceiptDate(data?.transactionDate)) ||
                              "N/A"}
                          </span>
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Payment Mode:{" "}
                          <span style={{ fontWeight: 400 }}>
                            {data?.paymentMode || "N/A"}
                          </span>
                        </Typography>
                      </Box>
                    </Box>
                    {/* Applicant Info */}
                    <Box
                      sx={{ bgcolor: "#F5F6F8", borderRadius: 1, p: 2, mb: 2 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Applicant Name:{" "}
                        <span style={{ fontWeight: 400 }}>
                          {data?.applicantName || "N/A"}
                        </span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Application ID:{" "}
                        {application_code && (
                          <span style={{ fontWeight: 400 }}>
                            {application_code || "N/A"}
                          </span>
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Application Type:{" "}
                        <span style={{ fontWeight: 400 }}>
                          {data?.applicationType || "N/A"}
                        </span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Contact No:{" "}
                        <span style={{ fontWeight: 400 }}>
                          {data?.contactNumber || "N/A"}
                        </span>
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Email ID:{" "}
                        <span style={{ fontWeight: 400 }}>
                          {data?.email || "N/A"}
                        </span>
                      </Typography>
                    </Box>
                    {/* Fee Table */}
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontWeight: 700,
                          borderBottom: "1px solid #e0e0e0",
                          pb: 1,
                          fontSize: 14,
                        }}
                      >
                        <span>Fee Head</span>
                        <span>Amount (₹)</span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 1,
                          borderBottom: "1px solid #e0e0e0",
                          fontSize: 14,
                        }}
                      >
                        <span>
                          {getHeaderSubmissionFee(data?.paymentStageId)}
                        </span>
                        <span>
                          {isROWPresent && data?.paymentStageId === 2
                            ? provisionalNOCAmount
                            : data?.paymentStageId === 1 &&
                                data?.fees?.penaltyAmount
                              ? data?.fees?.submittedFees -
                                data?.fees?.penaltyAmount
                              : data?.fees?.submittedFees}
                        </span>
                      </Box>
                      {(data?.paymentStage == "Processing fees" &&
                        data?.fees?.penaltyAmount &&
                        data?.fees?.penaltyAmount != 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 1,
                              borderBottom: "1px solid #e0e0e0",
                              fontSize: 14,
                            }}
                          >
                            <span>Penalty Charges</span>
                            <span>{data?.fees?.penaltyAmount || "N/A"}</span>
                          </Box>
                        )) ||
                        null}

                      {data?.paymentStageId === 2 && isROWPresent && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 1,
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: 14,
                          }}
                        >
                          <span>Final NOC Fee</span>
                          <span>
                            {data?.fees?.netAmount - provisionalNOCAmount ||
                              "N/A"}
                          </span>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 1,
                          bgcolor: "#F5F6F8",
                          fontWeight: 700,
                          borderRadius: 1,
                          fontSize: 14,
                        }}
                      >
                        <span>Total Paid</span>
                        <span>Rs {data?.fees?.totalAmount}</span>
                      </Box>
                    </Box>
                    {/* Payment Success */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <svg width="24" height="24" fill="none">
                        <circle cx="12" cy="12" r="12" fill="#4CAF50" />
                        <path
                          d="M7 13l3 3 7-7"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <Typography sx={{ color: "#4CAF50", fontWeight: 700 }}>
                        Payment Successful
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
                      This is a system-generated receipt for payment made
                      towards the licensing application. Please keep this
                      receipt for your records. No manual signature is required.
                    </Typography>
                    {/* Footer */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        color: "#888",
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          Authorized by
                          <br />
                          Ministry of Road Transport and Highways, Government of
                          India
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          For queries, contact
                          <br />
                          support-rmp@gov.in
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#888", textAlign: "center" }}
                >
                  No receipt data available.
                </Typography>
              )}
            </Box>
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1.2rem 1.5rem",
              borderTop: "1px solid #E7E6EB",
              background: "#fff",
            }}
          >
            <MuiButton
              type="button"
              name="Download"
              variant="outlined"
              fullWidth
              onClick={handleDownloadReceipt}
              sx={{
                border: "1px solid #3A1C73",
                color: `#fff`,
                borderRadius: "50px",
                fontWeight: `var(--font-regular)`,
                width: "fit-content",
                height: "unset",
                background: `#3A1C73`,
                transition: "all 0.3s",
              }}
            />
            <MuiButton
              type="button"
              name="Close"
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              sx={{
                border: "1px solid #3A1C73",
                color: `#3A1C73`,
                borderRadius: "50px",
                fontWeight: `var(--font-regular)`,
                width: "fit-content",
                height: "unset",
                transition: "all 0.3s",
                "&:hover": {
                  background: `#3A1C73`,
                  color: "#fff",
                },
              }}
            />
          </div>
        </Box>
      </Modal>
    </>
  );
}
